import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

export type RoleSearchMode = 'all' | 'area' | 'userBased';

interface RoleSearchFilterState {
  mode: RoleSearchMode;
  areaId: string | null;
  roleType?: 'position' | 'userBased';
}

interface RoleSearchFilterContextValue extends RoleSearchFilterState {
  setAreaFilter: (areaId: string | null) => void;
  setUserBasedMode: () => void;
  clearFilter: () => void;
}

const RoleSearchFilterContext =
  createContext<RoleSearchFilterContextValue | null>(null);

interface RoleSearchFilterProviderProps {
  children: React.ReactNode;
}

export function RoleSearchFilterProvider({
  children
}: RoleSearchFilterProviderProps) {
  const [state, setState] = useState<RoleSearchFilterState>({
    mode: 'all',
    areaId: null,
    roleType: undefined
  });

  const setAreaFilter = useCallback((areaId: string | null) => {
    if (!areaId) {
      setState({ mode: 'all', areaId: null, roleType: undefined });
      return;
    }
    setState({ mode: 'area', areaId, roleType: 'position' });
  }, []);

  const setUserBasedMode = useCallback(() => {
    setState({ mode: 'userBased', areaId: null, roleType: 'userBased' });
  }, []);

  const clearFilter = useCallback(() => {
    setState({ mode: 'all', areaId: null, roleType: undefined });
  }, []);

  const value = useMemo<RoleSearchFilterContextValue>(
    () => ({ ...state, setAreaFilter, setUserBasedMode, clearFilter }),
    [state, setAreaFilter, setUserBasedMode, clearFilter]
  );

  return (
    <RoleSearchFilterContext.Provider value={value}>
      {children}
    </RoleSearchFilterContext.Provider>
  );
}

export function useRoleSearchFilter(): RoleSearchFilterContextValue {
  const context = useContext(RoleSearchFilterContext);

  if (!context) {
    return {
      mode: 'all',
      areaId: null,
      roleType: undefined,
      setAreaFilter: () => {},
      setUserBasedMode: () => {},
      clearFilter: () => {}
    };
  }

  return context;
}
